import React from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';

import Layout from '../components/layout';
import SEO from '../components/seo';
import Heading3 from '../ui/Heading3';
import Blockquote from '../ui/Blockquote';

import { BREAKPOINTS, COLORS } from '../ui/variables';
import Heading1 from '../ui/Heading1';
import Heading2 from '../ui/Heading2';
import ParagraphLarge from '../ui/ParagraphLarge';
import ParagraphExLarge from '../ui/ParagraphExLarge';

const Container = styled.div`
  max-width: 1242px;
  padding: 0 40px;
  margin: auto;
`;

const TwoUp = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;

const TwoUpImg = styled.div`
  width: 48%;
  flex-shrink: 0;
  @media screen and (max-width: 600px) {
    width: 100%;
    margin-bottom: 49px;
  }
`;

const Hero = styled(TwoUp)`
  font-size: 24px;
  line-height: 33px;
  margin-bottom: 160px;
  margin-top: 30px;
  @media screen and (max-width: 600px) {
    margin-top: 20px;
    margin-bottom: 100px;
  }
`;

const List = styled.ul`
  max-width: 1026px;
  width: 100%;
  margin: 0;
  margin: auto;
  list-style: none;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 779px;
  }

  li {
    display: flex;
    margin-bottom: 60px;
    @media ${BREAKPOINTS.MOBILE} {
      flex-direction: column;
    }
  }
`;

const ItemDesc = styled.div`
  margin-left: 54px;
  @media ${BREAKPOINTS.MOBILE} {
    margin: 0;
  }
`;

const ItemLogo = styled.div`
  width: 160px;
  flex-shrink: 0;

  @media ${BREAKPOINTS.TABLET} {
    width: 120px;
  }
  @media ${BREAKPOINTS.MOBILE} {
    margin-bottom: 26px;
  }
`;

const BlueQuote = styled.div`
  padding: 213px 0;
  background: ${COLORS.BLUE};
  margin-bottom: 120px;

  @media ${BREAKPOINTS.TABLET} {
    padding: 162px 0;
  }

  @media ${BREAKPOINTS.MOBILE} {
    padding: 174px 40px;
    margin-bottom: 100px;
  }
`;

const OrangeBlock = styled.div`
  color: ${COLORS.ORANGE};
  text-transform: uppercase;
  margin-bottom: 32px;

  @media ${BREAKPOINTS.MOBILE} {
    margin-bottom: 24px;
  }
`;

const AccreditationsPage = () => {
  const data = useStaticQuery(graphql`
    query {
      heroImage: file(
        relativePath: { eq: "graphics/dog-person-back-to-back.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 540) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      vetsLeft: file(
        relativePath: { eq: "pages/accreditations/vets-left.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 594) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      vetsRight: file(
        relativePath: { eq: "pages/accreditations/vets-right.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 594) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      aahaLogo: file(
        relativePath: { eq: "pages/accreditations/aaha-logo.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 121) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      fearFree: file(
        relativePath: { eq: "pages/accreditations/fear-free-logo.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 121) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      bCorp: file(
        relativePath: { eq: "pages/accreditations/b-corp-logo.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 121) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      publicBenefit: file(
        relativePath: {
          eq: "pages/accreditations/public-benefit-corp-logo.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 121) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  const setOne = [
    {
      name: 'American Animal Hospital Association',
      desc:
        "The American Animal Hospital Association (AAHA) is the only organization that officially accredits veterinary practices in the US. As an accredited practice, we hold ourselves to a higher standard, and are routinely tested on over 900 different measures relating to the quality of care we provide to pets. We're proud to be amongst only 12-15% of hospitals with this honor.",
      fluidLogo: data.aahaLogo.childImageSharp.fluid,
    },
    {
      name: 'Fear Free Pets',
      desc:
        "Our team members are Fear Free Certified, which means they've all undergone intensive training courses to learn how to modify procedures, handling and our facilities to make pets feel safer and more comfortable at their appointments.",
      fluidLogo: data.fearFree.childImageSharp.fluid,
    },
  ];

  const setTwo = [
    {
      name: 'B Corporation',
      desc:
        "Small Door is a Certified B Corporation, which means we're held to a rigorous standard of social and environmental performance, public transparency, and accountability.",
      fluidLogo: data.bCorp.childImageSharp.fluid,
    },
    {
      name: 'Public Benefit Corporation',
      desc:
        "As a Public Benefit Corporation, we're not all about profit. As part of our founding charter, we commit to operating in ways that make the world a better place in concrete, measurable terms.",
      fluidLogo: data.publicBenefit.childImageSharp.fluid,
    },
  ];

  return (
    <Layout>
      <SEO title='Accreditations' />
      <Container>
        <Hero>
          <div
            css={css`
              max-width: 540px;
              width: 50%;
              margin-right: 100px;
              @media ${BREAKPOINTS.MOBILE} {
                width: 100%;
                margin-right: 48px;
              }
              @media screen and (max-width: 600px) {
                width: 100%;
                margin: 0;
              }
            `}
          >
            <Heading1>We're in good company</Heading1>
            <ParagraphExLarge>
              At Small Door, we're committed to changing the veterinary
              experience for the better. We're proud to have been recognized
              with a number of industry accreditations and certifications.
            </ParagraphExLarge>
          </div>

          <div
            css={css`
              background-color: ${COLORS.BEIGE};
              width: 50%;
              padding: 74px 26px 35px 26px;
              flex-shrink: 0;
              @media screen and (max-width: 600px) {
                width: 100%;
                margin: 0 0 49px 0;
                order: -1;
              }
            `}
          >
            <Img fluid={data.heroImage.childImageSharp.fluid} />
          </div>
        </Hero>

        <TwoUp
          css={css`
            margin-bottom: 100px;
          `}
        >
          <TwoUpImg>
            <Img fluid={data.vetsLeft.childImageSharp.fluid} />
          </TwoUpImg>
          <div
            css={css`
              margin-left: 108px;
              @media ${BREAKPOINTS.MOBILE} {
                margin: 0;
              }
            `}
          >
            <OrangeBlock>Veterinary Accreditations</OrangeBlock>
            <Heading2>
              We're committed to treating your pets as we do our own
            </Heading2>
            <ParagraphLarge>
              With us, your pet's well-being always comes first. We pride
              ourselves on delivering first-class care in a number of ways.
            </ParagraphLarge>
          </div>
        </TwoUp>

        <List>
          {setOne.map(item => (
            <li key={item.name}>
              <ItemLogo>
                <Img fluid={item.fluidLogo} />
              </ItemLogo>
              <ItemDesc>
                <Heading3
                  css={css`
                    padding-bottom: 16px;
                  `}
                >
                  {item.name}
                </Heading3>
                {item.desc}
              </ItemDesc>
            </li>
          ))}
        </List>
      </Container>

      <BlueQuote>
        <Blockquote
          css={css`
            max-width: 864px;
            margin: auto;
            text-align: center;
            @media ${BREAKPOINTS.TABLET} {
              max-width: 700px;
            }
          `}
        >
          "We'll never stop striving to deliver exceptional care for pets –
          because your pets deserve nothing less."
        </Blockquote>
      </BlueQuote>

      <Container>
        <TwoUp
          css={css`
            margin-bottom: 100px;
          `}
        >
          <div
            css={css`
              margin-right: 108px;
              @media ${BREAKPOINTS.MOBILE} {
                margin: 0;
              }
            `}
          >
            <OrangeBlock>Company Accreditations</OrangeBlock>
            <Heading2>
              We're held to a rigorous standard of transparency and
              accountability
            </Heading2>
            <ParagraphLarge>
              To make a difference in the veterinary world, we need to be
              different. Small Door was founded to improve the lives of
              veterinarians and veterinary nurses. With every decision we make,
              we take their well-being and success into account.
            </ParagraphLarge>
          </div>

          <TwoUpImg
            css={css`
              @media ${BREAKPOINTS.MOBILE} {
                order: -1;
              }
            `}
          >
            <Img fluid={data.vetsRight.childImageSharp.fluid} />
          </TwoUpImg>
        </TwoUp>

        <List>
          {setTwo.map(item => (
            <li key={item.name}>
              <ItemLogo>
                <Img fluid={item.fluidLogo} />
              </ItemLogo>
              <ItemDesc>
                <Heading3
                  css={css`
                    padding-bottom: 16px;
                  `}
                >
                  {item.name}
                </Heading3>
                {item.desc}
              </ItemDesc>
            </li>
          ))}
        </List>
      </Container>
    </Layout>
  );
};

export default AccreditationsPage;
